import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { UPDATE_HHG_SEGMENT_FAILURE } from 'modules/authorizations/store/types/houseHoldGoodsTypes';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import { getHhgSegments, updateHhgSegment } from 'modules/authorizations/store/actions/houseHoldGoods.actions';
import EditableCard from 'modules/common/editableCard.component';
import React, { useMemo, useState } from 'react';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';

const SegmentInfoCard = props => {
  const [isLoading, setIsLoading] = useState(false);

  const { segmentInfo, storageAddress, vendors, billToEnums, vendorTypes, id, enumerations } = props;

  const movePreferenceTabs = [{ header: 'Move Preferences', order: 1, iconPath: ['fas', 'suitcase'] }];

  const billToOptions =
    billToEnums && billToEnums.length > 0
      ? billToEnums.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  const vendorSearchRows = useMemo(
    () =>
      (vendors || []).map(v => ({
        ...v,
        vendorTypeName: (vendorTypes || []).find(t => isIdEqual(t.id, v.vendorTypeId))?.title ?? ''
      })),
    [vendors, vendorTypes]
  );

  const vendorSearchInfo = {
    searchingColumns: [
      { name: 'vendorId', title: 'Id' },
      { name: 'vendorTypeName', title: 'Type of Vendor' },
      { name: 'companyName', title: 'Company Name' }
    ],
    searchingColumnExtensions: [
      { columnName: 'vendorId', width: 100 },
      { columnName: 'vendorTypeName', width: 200 },
      { columnName: 'companyName', width: 300 }
    ],
    searchingRows: vendorSearchRows,
    nameKey: 'companyName',
    idKey: 'vendorId'
  };

  const countries =
    enumerations && enumerations.countries && enumerations.countries.length > 0
      ? enumerations.countries.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];
  const provinces =
    enumerations && enumerations.provinces && enumerations.provinces.length > 0
      ? enumerations.provinces.filter(s => s.isActive === true)
      : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];

  let selectedVendor = null;

  if (vendors && segmentInfo) {
    selectedVendor = vendors.find(s => s.vendorId === segmentInfo?.segmentInfo.vendorId);
  } // location, unit size, adr? - they'll give us a starting point, something to start with. They'll start with an excel sheet to start with. Once a month, once a quarter? Tuesday of next week they'll give us the initial batch of data

  // Populated from the intake form on submit. Hidden on this card for now, in both view and edit. They stay in the
  // summaries so the edit modal carries their stored values through a save untouched. To show them again, drop the
  // hide flags: they belong after Address when viewing and after Delivery Goal when editing.
  const callCompleteSummaries = [
    {
      header: 'Timeframe Call Complete',
      accessorKey: 'isTimeframeCallComplete',
      type: 'checkbox',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.isTimeframeCallComplete : false,
      hideInTable: true,
      hideInModal: true,
      tab: 1
    },
    {
      header: 'Packing Instructions Call Complete',
      accessorKey: 'isPackingInstructionsCallComplete',
      type: 'checkbox',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.isPackingInstructionsCallComplete : false,
      hideInTable: true,
      hideInModal: true,
      tab: 1
    }
  ];

  const movePreferenceSummaries = [
    {
      header: 'Bill To',
      accessorKey: 'billToId',
      type: 'dropdown-v2',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.billToId : '',
      options: billToOptions,
      tab: 1
    },
    {
      header: 'Monetary Cap',
      accessorKey: 'hasMonetaryCap',
      type: 'checkbox',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.hasMonetaryCap : '',
      tab: 1
    },
    {
      header: 'Monetary Cap',
      accessorKey: 'monetaryCap',
      type: 'money',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.monetaryCap : '',
      tab: 1,
      dependentOn: 'hasMonetaryCap'
    },
    {
      header: 'Weight Cap',
      accessorKey: 'weightCap',
      type: 'number',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.weightCap : 0,
      tab: 1
    },
    {
      header: 'Client Disc Rt',
      accessorKey: 'clientDiscRt',
      type: 'text',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.clientDiscRt : '',
      tab: 1
    },
    {
      header: 'Insured Amount',
      accessorKey: 'insuredAmount',
      type: 'money',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.insuredAmount : 0,
      tab: 1
    },
    {
      header: 'Pref Survey On',
      accessorKey: 'prefSurveyOn',
      type: 'date',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.prefSurveyOn : '',
      tab: 1
    },
    {
      header: 'Pref Pack/Load',
      accessorKey: 'prefPackLoad',
      type: 'date',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.prefPackLoad : '',
      tab: 1
    },
    {
      header: 'Delivery Goal',
      accessorKey: 'deliveryGoal',
      type: 'date',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.deliveryGoal : '',
      tab: 1
    },
    {
      header: 'Large/Bulky Items',
      accessorKey: 'largeItems',
      type: 'notes',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.largeItems : '',
      tab: 1
    },
    {
      header: 'Vendor Instructions',
      accessorKey: 'vendorInstructions',
      type: 'notes',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.vendorInstructions : '',
      tab: 1
    },
    {
      header: 'Vendor',
      accessorKey: 'vendorId',
      nameKey: 'companyName',
      nameValue: selectedVendor ? selectedVendor.companyNameDisplay : '',
      type: 'advanced-search',
      searchInfo: vendorSearchInfo,
      value: segmentInfo?.segmentInfo?.vendorId || 0,
      tab: 1
    },
    {
      header: 'Address',
      accessorKey: 'editedAddress',
      type: 'googleAddressSearch',
      required: false,
      value: storageAddress ? storageAddress : '',
      displayValue: storageAddress ? storageAddress.addressLine1 : '',
      tab: 1,
      provinces: provinces,
      countries: countries
    },
    ...callCompleteSummaries
  ];

  const selfMoveSummaries = [
    {
      header: 'Bill To',
      accessorKey: 'billToId',
      type: 'dropdown-v2',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.billToId : '',
      options: billToOptions,
      tab: 1
    },
    {
      header: 'Monetary Cap',
      accessorKey: 'hasMonetaryCap',
      type: 'checkbox',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.hasMonetaryCap : '',
      tab: 1
    },
    {
      header: 'Monetary Cap',
      accessorKey: 'monetaryCap',
      type: 'money',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.monetaryCap : '',
      tab: 1,
      dependentOn: 'hasMonetaryCap'
    },
    {
      header: 'Delivery Goal',
      accessorKey: 'deliveryGoal',
      type: 'date',
      required: false,
      value: segmentInfo ? segmentInfo.segmentInfo.deliveryGoal : '',
      tab: 1
    }
  ];

  // Cleared inputs come through as '' — the backend's nullable decimal/DateTime fields need null instead.
  const toNullableNumber = value => (value == null || value === '' ? null : Number(value));
  const toNullableDate = value => (value == null || value === '' ? null : value);

  const updateFields = async (tab, values) => {
    setIsLoading(true);

    let newValues = { ...segmentInfo.segmentInfo };

    newValues.billToId = values.billToId;
    newValues.hasMonetaryCap = values.hasMonetaryCap === true;
    newValues.monetaryCap = newValues.hasMonetaryCap ? toNullableNumber(values.monetaryCap) : null;
    newValues.deliveryGoal = toNullableDate(values.deliveryGoal);

    if (segmentInfo && segmentInfo.segmentInfo && segmentInfo.segmentInfo.hhgTypeId !== '83300e16-71cc-408f-8c1b-5e637eee7618') {
      newValues.weightCap = toNullableNumber(values.weightCap);
      newValues.prefSurveyOn = toNullableDate(values.prefSurveyOn);
      newValues.prefPackLoad = toNullableDate(values.prefPackLoad);
      newValues.clientDiscRt = values.clientDiscRt;
      newValues.insuredAmount = toNullableNumber(values.insuredAmount);
      newValues.largeItems = values.largeItems;
      newValues.vendorInstructions = values.vendorInstructions;
      newValues.vendorId = values.vendorId && !Array.isArray(values.vendorId) ? values.vendorId : null;
      newValues.isTimeframeCallComplete = values.isTimeframeCallComplete === true;
      newValues.isPackingInstructionsCallComplete = values.isPackingInstructionsCallComplete === true;
    }

    // The backend always keeps an address row for the segment (a placeholder when nothing was entered), so the
    // received storageAddress object must be echoed back in full with edits merged on top — never null.
    // GoogleAddressSearch emits provinceId/countryId (never province/country) and has no addressLine2 input,
    // so those fall back to the stored address rather than getting dropped from the payload.
    const basicSegmentInfo = {
      segmentInfo: { ...newValues },
      storageAddress: {
        ...storageAddress,
        addressLine1: values.editedAddress ? values.editedAddress.addressLine1 : storageAddress?.addressLine1,
        addressLine2: values.editedAddress?.addressLine2 ?? storageAddress?.addressLine2,
        city: values.editedAddress ? values.editedAddress.city : storageAddress?.city,
        county: values.editedAddress ? values.editedAddress.county : storageAddress?.county,
        provinceId: values.editedAddress?.provinceId || storageAddress?.provinceId,
        zipcode: values.editedAddress ? values.editedAddress.zipcode : storageAddress?.zipcode,
        countryId: values.editedAddress?.countryId || storageAddress?.countryId
      }
    };

    if (segmentInfo && segmentInfo.length !== 0) {
      const resp = await props.updateHhgSegment(basicSegmentInfo);
      if (resp.type === UPDATE_HHG_SEGMENT_FAILURE) {
        props.showToast('Failed to update the segment info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        setIsLoading(false);
        return false;
      } else {
        await props.getHhgSegments(id);
        setIsLoading(false);
        return true;
      }
    }
  };

  return (
    <EditableCard
      tabs={movePreferenceTabs}
      summaries={
        segmentInfo && segmentInfo.segmentInfo && segmentInfo.segmentInfo.hhgTypeId === '83300e16-71cc-408f-8c1b-5e637eee7618'
          ? selfMoveSummaries
          : movePreferenceSummaries
      }
      numColumns={5}
      updateFields={updateFields}
      isLoading={isLoading}
      hideEdit={props.hideEdit}
    />
  );
};

const mapStateToProps = state => {
  const { billTo, vendorTypes } = state.enumerations.allEnums;
  return {
    billToEnums: billTo,
    vendorTypes
  };
};

export default compose(
  connect(mapStateToProps, {
    updateHhgSegment,
    showToast,
    getHhgSegments
  })
)(SegmentInfoCard);